import { User } from "@/lib/api/user";
import { getPreferredName } from "@/lib/redacted-display";
import { useEnterTrainingSession } from "@/lib/hooks/training-simulator/use-enter-training-session.ts";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button, UserAvatar } from "@somethings-ui/web";
import { useState } from "react";
import { TrainingLeadMentorModal } from "./session/training-lead-mentor-modal";

interface TrainingHomeHeaderProps {
  user: User | undefined;
  currentAttemptId?: string | null;
  nextLevelId?: string | null;
}

export function TrainingHomeHeader({
  user,
  currentAttemptId,
  nextLevelId,
}: TrainingHomeHeaderProps) {
  const firstName = user ? getPreferredName(user) : "";
  const imageUrl = useSignedMedia(user?.profile_picture_url ?? "");

  const [isLeadMentorModalOpen, setIsLeadMentorModalOpen] = useState(false);

  const { enterSession, goToSession, isStarting } = useEnterTrainingSession();
  const { track } = useTrackEvent();

  const canStartSession = Boolean(currentAttemptId || nextLevelId);

  const handleStartSession = () => {
    track(PostHogEvents.SIMULATOR_START_SESSION_CTA_CLICK, {
      has_current_attempt: Boolean(currentAttemptId),
    });
    if (currentAttemptId) {
      goToSession(currentAttemptId);
      return;
    }
    if (nextLevelId) {
      enterSession(nextLevelId, "start");
    }
  };

  return (
    <header className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-header-2 text-text-primary">Welcome back,</h1>
          <UserAvatar name={firstName} imageUrl={imageUrl.url} size="md" />
          <h1 className="text-header-2 text-text-primary">{firstName}.</h1>
        </div>
        <p className="max-w-md text-body-1 text-text-secondary">
          Work through the courses to practice real conversations and grow your
          mentoring skills.
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          fullWidth={false}
          onClick={() => {
            track(PostHogEvents.SIMULATOR_LEAD_MENTOR_MESSAGE_MODAL_OPEN, {
              source: "training_home",
            });
            setIsLeadMentorModalOpen(true);
          }}
        >
          Message Lead Mentor
        </Button>
        {canStartSession && (
          <Button
            variant="primary"
            fullWidth={false}
            disabled={!canStartSession}
            loading={isStarting}
            onClick={handleStartSession}
          >
            Start a session
          </Button>
        )}
      </div>

      <TrainingLeadMentorModal
        open={isLeadMentorModalOpen}
        onOpenChange={setIsLeadMentorModalOpen}
      />
    </header>
  );
}
